/**
 * OnboardingBanner — Banner compacto que se muestra en el Dashboard del titular
 * cuando el onboarding del negocio reclamado no está completo (< 100%).
 *
 * Se oculta automáticamente cuando is_complete=true o si el usuario lo descarta.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Confetti, ArrowRight, X } from "@phosphor-icons/react";

export default function OnboardingBanner({ role }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (role !== "admin") return;
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get("/admin/onboarding/status");
        if (!cancelled) setData(d);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [role]);

  const dismiss = async () => {
    try {
      await api.post("/admin/onboarding/dismiss", {});
      setData((d) => d ? { ...d, dismissed: true } : d);
    } catch { /* ignore */ }
  };

  if (!data) return null;
  if (data.is_complete) return null;
  if (data.dismissed) return null;

  const pct = data.progress_pct;

  return (
    <div className="relative border border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10 rounded-lg p-4 sm:p-5" data-testid="dashboard-onboarding-banner">
      <button
        type="button"
        onClick={dismiss}
        aria-label="Ocultar banner"
        className="absolute top-2 right-2 text-[var(--ag-text-dim)] hover:text-white p-1 rounded"
        data-testid="onboarding-banner-dismiss"
      >
        <X size={14} />
      </button>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 pr-6">
        <Confetti size={32} weight="fill" className="text-[var(--ag-gold)] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[var(--ag-text)] text-sm sm:text-base">
            Configura tu negocio para empezar a recibir leads
          </h3>
          <p className="text-xs text-[var(--ag-text-dim)] mt-0.5">
            {data.completed} de {data.total} pasos completados · Te llevará 5 minutos
          </p>
          <div className="mt-2 h-1.5 w-full max-w-md bg-[var(--ag-midnight-soft)] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[var(--ag-gold)] to-[var(--ag-gold-strong)] transition-all duration-500"
              style={{ width: `${pct}%` }}
              data-testid="onboarding-banner-progress"
            />
          </div>
        </div>
        <Link
          to="/onboarding"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--ag-gold)] text-[var(--ag-midnight)] rounded text-sm font-semibold hover:bg-[var(--ag-gold-strong)] flex-shrink-0"
          data-testid="onboarding-banner-cta"
        >
          Continuar <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    </div>
  );
}
